import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react'
import {
  DraftConjointItem,
  ResponseOptionMediaInput,
  UpdateConjointItemArgsInput
} from '../../../../data/gql-gen/questionnaire/graphql'
import PackageRestrictionsDialog from './PackageRestrictionsDialog'

const SAVE_ERROR_MESSAGE =
  'Could not save package restrictions. Please try again.'

export interface ConjointStateOption {
  attributeOptionId: string
  position: number
  value: string
  /** Wrapped like the GQL input (see draftConjointItem contract) so it can be
   * resent to the backend unchanged whenever this option is included in a
   * save, without ever accidentally clearing a stored image. */
  media: { value: ResponseOptionMediaInput | null }
}

export interface ConjointStateAttribute {
  attributeId: string
  name: string
  position: number
  isAppearance: boolean
  options: ConjointStateOption[]
}

export interface ConjointState {
  text: string
  textStyling?: string | null
  attributes: ConjointStateAttribute[]
}

interface PackageRestrictionsDialogContextValue {
  /** True when this attribute option is restricted to a subset of packages. */
  isOptionRestricted: (attributeOptionId: string) => boolean
  openPackageRestrictions: () => void
}

const PackageRestrictionsDialogContext =
  createContext<PackageRestrictionsDialogContextValue | null>(null)

/**
 * Access the current conjoint card's package-restrictions dialog. Returns null
 * outside a provider, so callers (e.g. ConjointAttributeItem in isolation, in
 * tests) must guard with `?.`.
 */
export const usePackageRestrictionsDialog =
  (): PackageRestrictionsDialogContextValue | null =>
    useContext(PackageRestrictionsDialogContext)

interface Props {
  /** Raw (server-synced) attributes — the only place allowedPackages currently lives. */
  entryAttributes: DraftConjointItem['attributes']
  /** Local live truth for attribute/option text and structure (see ConjointQuestion.container). */
  conjointState: ConjointState
  panelSize: number
  // Apollo resolves (rather than rejects) a mutation whose hook was given an
  // onError option — which useUpdateConjoint's is, for the debounced/fire-and-
  // forget edit paths' logging — so a failed save surfaces as a resolved
  // result carrying `errors`, not a rejection. See handleSave below.
  updateConjoint: (
    input: UpdateConjointItemArgsInput
  ) => Promise<{ errors?: readonly unknown[] } | null | undefined>
  cancelPendingUpdate: () => void
  saving: boolean
  children: ReactNode
}

/**
 * Hosts the conjoint card's PackageRestrictionsDialog and exposes an
 * `openPackageRestrictions` trigger plus per-option restricted state via
 * context, so the trigger button (next to ConjointSettings) and the read-only
 * markers on each value row (ConjointAttributeItem) — different subtrees of the
 * same card — can both reach it without prop-drilling.
 */
export const PackageRestrictionsDialogProvider: FC<Props> = ({
  entryAttributes,
  conjointState,
  panelSize,
  updateConjoint,
  cancelPendingUpdate,
  saving,
  children
}) => {
  const [open, setOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  // conjointState never carries allowedPackages (it's not part of the debounced
  // text/name/value editing loop) — the raw entry is the only source for it.
  // Backend semantics: null = unrestricted, [] = eligible nowhere — keep null
  // distinct from [] rather than normalising, or the dialog can't tell an
  // untouched value apart from one broken by legacy/invalid data.
  const savedAllowedPackages = useMemo(() => {
    const map = new Map<string, number[] | null>()
    entryAttributes.forEach((attribute) => {
      attribute.options.forEach((option) => {
        map.set(option.attributeOptionId, option.allowedPackages ?? null)
      })
    })
    return map
  }, [entryAttributes])

  const restrictedOptionIds = useMemo(() => {
    const ids = new Set<string>()
    savedAllowedPackages.forEach((allowedPackages, optionId) => {
      if (allowedPackages != null) {
        ids.add(optionId)
      }
    })
    return ids
  }, [savedAllowedPackages])

  const value = useMemo(
    () => ({
      isOptionRestricted: (attributeOptionId: string) =>
        restrictedOptionIds.has(attributeOptionId),
      openPackageRestrictions: () => setOpen(true)
    }),
    [restrictedOptionIds]
  )

  // Explicit allowedPackages for every option, wrapped in the
  // OptionalFieldListIntInput the backend now expects — omitting the
  // wrapper entirely means "preserve" server-side, which would silently
  // no-op an un-tick-everything save. { value: null } is unrestricted;
  // { value: [] } would mean eligible nowhere at the backend, not everywhere.
  const handleSave = async (
    allowedPackagesByOptionId: Map<string, number[] | null>
  ) => {
    cancelPendingUpdate()
    try {
      const result = await updateConjoint({
        text: conjointState.text,
        textStyling: conjointState.textStyling,
        attributes: conjointState.attributes.map((attribute) => ({
          attributeId: attribute.attributeId,
          name: attribute.name,
          position: attribute.position,
          isAppearance: attribute.isAppearance,
          options: attribute.options.map((option) => ({
            attributeOptionId: option.attributeOptionId,
            position: option.position,
            value: option.value,
            // Resend the stored image explicitly — this dialog rebuilds each
            // option field-by-field rather than spreading it, so a stored
            // media would otherwise be silently dropped from this save.
            media: option.media,
            allowedPackages: {
              value:
                allowedPackagesByOptionId.get(option.attributeOptionId) ?? null
            }
          }))
        }))
      })
      // useUpdateConjoint's onError makes Apollo resolve rather than reject on
      // failure, so a failed save must be detected from the result, not caught.
      if (result?.errors?.length) {
        throw new Error('Failed to save package restrictions')
      }
      setOpen(false)
    } catch {
      // Keep the dialog open so the user can retry the save.
      enqueueSnackbar(SAVE_ERROR_MESSAGE, { variant: SnackbarVariant.Error })
    }
  }

  return (
    <PackageRestrictionsDialogContext.Provider value={value}>
      {children}
      <PackageRestrictionsDialog
        open={open}
        attributes={conjointState.attributes}
        panelSize={panelSize}
        savedAllowedPackages={savedAllowedPackages}
        saving={saving}
        onClose={() => setOpen(false)}
        onSave={handleSave}
      />
    </PackageRestrictionsDialogContext.Provider>
  )
}
